import apiClient from './api';

export interface MotifRefoulement {
  id: string;
  libelle: string;
  description: string;
  categorie: string;
  criticite: 'Critique' | 'Haute' | 'Moyenne' | 'Faible';
  code: string;
  // Propriétés mappées pour l'affichage
  nom?: string;
}

export interface RefoulementData {
  pesee_id: number;
  motifs: string[];
  observations: string;
}

export interface RefoulementResponse {
  id: number;
  pesee: number;
  pesee_code: string;
  pesee_reference: string;
  numero_refoulement: string;
  motifs: MotifRefoulement[];
  motifs_display: string;
  observations: string;
  agent_refoulement: number;
  agent_refoulement_nom: string;
  agent_refoulement_username: string;
  date_refoulement: string;
  date_refoulement_display: string;
  created_at: string;
  updated_at: string;
}

export interface RefoulementTicketData {
  refoulement: RefoulementResponse;
  pesee: any; // Type Pesée complet
  generated_at: string;
}

class RefoulementService {
  private static instance: RefoulementService;

  private constructor() {}

  public static getInstance(): RefoulementService {
    if (!RefoulementService.instance) {
      RefoulementService.instance = new RefoulementService();
    }
    return RefoulementService.instance;
  }

  /**
   * Récupérer tous les motifs de refoulement disponibles
   */
  async getMotifsRefoulement(): Promise<MotifRefoulement[]> {
    try {
      const response = await apiClient.get('/weighing/api/refoulements/motifs-refoulement/');
      
      if (response.data && response.data.success) {
        return response.data.data || [];
      } else {
        throw new Error(response.data?.message || 'Erreur lors de la récupération des motifs');
      }
    } catch (error) {
      console.error('Erreur lors de la récupération des motifs de refoulement:', error);
      throw error;
    }
  }

  /**
   * Créer un refoulement pour une pesée
   */
  async creerRefoulement(data: RefoulementData): Promise<RefoulementResponse> {
    try {
      const payload = {
        pesee: data.pesee_id,
        motifs: data.motifs,
        observations: data.observations
      }; 
      const response = await apiClient.post('/weighing/api/refoulements/creer-refoulement/', payload);

      if (response.data && response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data?.message || 'Erreur lors de la création du refoulement');
      }
    } catch (error: any) {
      throw error;
    }
  }

  /**
   * Récupérer la liste des refoulements
   */
  async getRefoulements(params?: {
    page?: number;
    page_size?: number;
    search?: string;
    pesee_code?: string;
    agent_refoulement?: number;
    date_from?: string;
    date_to?: string;
  }): Promise<{
    results: RefoulementResponse[];
    count: number;
    next: string | null;
    previous: string | null;
  }> {
    try {
      const response = await apiClient.get('/weighing/api/refoulements/', { params });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Récupérer un refoulement par ID
   */
  async getRefoulementById(id: number): Promise<RefoulementResponse> {
    try {
      const response = await apiClient.get(`/weighing/api/refoulements/${id}/`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Générer le ticket PDF de refoulement
   */
  async generateTicketPDF(refoulementId: number): Promise<RefoulementTicketData> {
    try {
      const response = await apiClient.get(`/weighing/api/refoulements/${refoulementId}/ticket-pdf/`);
      
      if (response.data && response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data?.message || 'Erreur lors de la génération du ticket');
      }
    } catch (error) {
      throw error;
    }
  }

  /**
   * Vérifier si une pesée peut être refoulée
   */
  async canRefoulerPesee(peseeId: number): Promise<boolean> {
    try {
      const response = await apiClient.get(`/weighing/api/pesees/${peseeId}/`);
      const pesee = response.data;
      
      // Une pesée peut être refoulée si elle n'est pas déjà refoulée
      return pesee.statut !== 'REFOULEE';
    } catch (error) {
      return false;
    }
  }

  /**
   * Récupérer les motifs groupés par catégorie
   */
  async getMotifsGroupes(): Promise<Record<string, MotifRefoulement[]>> {
    try {
      const motifs = await this.getMotifsRefoulement();
      const groupes: Record<string, MotifRefoulement[]> = {};

      motifs.forEach(motif => {
        const categorie = motif.categorie || 'Autres';
        if (!groupes[categorie]) {
          groupes[categorie] = [];
        }
        groupes[categorie].push(motif);
      });

      return groupes;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Rechercher des motifs par terme
   */
  async searchMotifs(searchTerm: string): Promise<MotifRefoulement[]> {
    try {
      const motifs = await this.getMotifsRefoulement();
      const term = searchTerm.toLowerCase();
      
      return motifs.filter(motif => 
        motif.nom?.toLowerCase().includes(term) ||
        motif.description?.toLowerCase().includes(term) ||
        motif.categorie?.toLowerCase().includes(term)
      );
    } catch (error) {
      throw error;
    }
  }

  /**
   * Valider les données de refoulement
   */
  validateRefoulementData(data: RefoulementData): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];

    if (!data.pesee_id || data.pesee_id <= 0) {
      errors.push('ID de la pesée requis');
    }

    if (!data.motifs || data.motifs.length === 0) {
      errors.push('Au moins un motif doit être sélectionné');
    }

    if (data.observations && data.observations.length > 1000) {
      errors.push('Les observations ne peuvent pas dépasser 1000 caractères');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }
}

// Export de l'instance singleton
export default RefoulementService.getInstance();
